/* eslint-disable max-len */
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideCircleHelp,
  lucideCirclePlay,
  lucideCopy,
  lucideEye,
  lucideLock,
  lucidePenLine,
  lucideUsers,
  lucideX,
} from '@ng-icons/lucide';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import {
  deriveFlowModeFromStored,
  isUsableAlbum,
  selectionAskCounts,
  type ProjectFlowMode,
  type SelectionAsk,
} from '@lite-workspace/dto-fotoclass-book';
import { pluralRu } from '@lite-workspace/shared/format';
import { DialogSheetComponent } from '@lite-workspace/shared/helm-ui/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmSkeleton } from '@lite-workspace/shared/helm-ui/skeleton';
import { HlmTabsImports } from '@lite-workspace/shared/helm-ui/tabs';
import { HlmTooltip } from '@lite-workspace/shared/helm-ui/tooltip';
import { OnboardingAnchorDirective, OnboardingHelpDirective } from '@lite-workspace/shared/onboarding-service';
import { ProjectsFacade } from '../domain/application/projects.facade';
import { ShareAnalyticsService } from '../domain/application/share-analytics.service';
import { CoverGateCardComponent } from './cover-gate-card.component';
import { PersonalLinksStubComponent } from './personal-links-stub.component';
import { HlmShareLinkField } from '@lite-workspace/shared/helm-ui/share-link-field';
import { WizardHelpButtonComponent } from '@lite-workspace/shared/blocks/wizard-shell';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { clientZoneUrl, formatKeysClipboard, personalLinkUrl } from '../domain/utils/person-keys.util';
import type { Person, StudentLoginMode } from '../domain/entities/project.models';
import type { StepCounts } from './step-chip.component';

export { clientZoneUrl };

export interface OrganizerShareContext {
  projectId: string;
  /** Pre-fetched links (fast path). Omit them and the dialog fetches its own on open, so the modal
   *  appears instantly instead of after a share round-trip. */
  link?: string;
  coordinatorCode?: string;
  organizerOnly?: boolean;
  /** «Отправить группе» — the organizer's own share: only the class/group link, no organizer link. */
  groupMode?: boolean;
  /** «Скопировать все ссылки» (links mode) — only the personal-links list, no organizer link. */
  linksOnly?: boolean;
  /** «Скопировать все ПИНы» (pin mode) — only the pins list, no organizer link. */
  pinsOnly?: boolean;
  /** The ORGANIZER opened this window, not the photographer: own banner copy, no «альбомы» link
   *  (the albums tab is not his). Without the flag the window stays byte-identical. */
  organizerView?: boolean;
  /** Open tab of «Отправить заказчику»; students when omitted. */
  tab?: ShareTab;
  /** Class progress from the albums page; absent = the students subtitle speaks as for a fresh class. */
  stepCounts?: StepCounts | null;
}

export type ShareTab = 'students' | 'organizer';

/** «Отправить заказчику»: no narrowing flag — the owner's window with both links. */
export function isCustomerShare(
  mode: Pick<OrganizerShareContext, 'organizerOnly' | 'groupMode' | 'linksOnly' | 'pinsOnly'>,
): boolean {
  return !mode.organizerOnly && !mode.groupMode && !mode.linksOnly && !mode.pinsOnly;
}

/** Flow of the cached project settings; `null` = settings not read yet. */
export function shareFlowMode(facade: ProjectsFacade, projectId: string): ProjectFlowMode | null {
  const settings = facade.baseSettingsByProject()[projectId];
  return settings ? deriveFlowModeFromStored(settings.photoBook?.clientSelection) : null;
}

const COPIED_MS = 3000;

const NBSP = ' ';
const ASK_LEAD = 'Отправьте ссылку в чат класса — каждый ученик';
/** Числительные 2–7 для «уже отобрали» (fork 03); дальше — число. */
const COLLECTIVE = ['', '', 'Двое', 'Трое', 'Четверо', 'Пятеро', 'Шестеро', 'Семеро'];

function pickedSentence(picked: number): string {
  if (picked === 1) return 'Один ученик уже отобрал фото и по ссылке откроет свой альбом.';
  if (picked < COLLECTIVE.length) return `${COLLECTIVE[picked]} уже отобрали и по ссылке откроют свой альбом.`;
  const who = pluralRu(picked, ['ученик уже отобрал', 'ученика уже отобрали', 'учеников уже отобрали']);
  const verb = pluralRu(picked, ['откроет', 'откроют', 'откроют']);
  return `${picked}${NBSP}${who} фото и по ссылке ${verb} свой альбом.`;
}

function askPhrase(ask: SelectionAsk | null): string {
  const parts = [
    ask?.portraits ? `${ask.portraits}${NBSP}${pluralRu(ask.portraits, ['портрет', 'портрета', 'портретов'])}` : '',
    ask?.groups ? `${ask.groups}${NBSP}${pluralRu(ask.groups, ['групповое', 'групповых', 'групповых'])}` : '',
  ].filter(Boolean);
  if (parts.length === 0) return 'свои фото';
  return ask?.groups ? `${parts.join(' и ')} фото` : parts[0];
}

/** Every student picked: the subtitle line needs no etalon structure. */
function allPicked(counts: StepCounts | null): boolean {
  const total = counts?.total ?? 0;
  return total > 0 && (counts?.selected ?? 0) >= total;
}

/** Подзаголовок вкладки «Ученикам» по состоянию класса (R13). `flow` null — настройки ещё не прочитаны. */
export function studentsShareSubtitle(flow: ProjectFlowMode | null, ask: SelectionAsk | null, counts: StepCounts | null): string {
  if (!flow) return `${ASK_LEAD} выберет ${askPhrase(null)}`;
  if (flow === 'auto') return `${ASK_LEAD} откроет свой альбом`;
  const total = counts?.total ?? 0;
  const picked = counts?.selected ?? 0;
  if (allPicked(counts)) {
    const head =
      total === 1
        ? 'Ученик отобрал фото. По ссылке он'
        : `Все ${total}${NBSP}${pluralRu(total, ['ученик', 'ученика', 'учеников'])} отобрали фото. По ссылке каждый`;
    return flow === 'studentAssembles'
      ? `${head} соберёт и утвердит свой альбом.`
      : `${head} утвердит свой альбом, когда вы отправите макеты на утверждение.`;
  }

  const line = `${ASK_LEAD} выберет ${askPhrase(ask)}`;
  return picked > 0 ? `${line}. ${pickedSentence(picked)}` : line;
}

@Component({
  selector: 'app-organizer-share-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class:
      'block dlg-phone:contents dlg-desktop:w-[var(--dlg-w-share)] dlg-desktop:rounded-[var(--r-xl)] dlg-desktop:border dlg-desktop:border-line2 dlg-desktop:bg-panel dlg-desktop:p-10 dlg-desktop:shadow-[var(--shadow-4)]',
  },
  imports: [
    HlmButton,
    DialogSheetComponent,
    HlmTooltip,
    NgIcon,
    PersonalLinksStubComponent,
    HlmShareLinkField,
    CoverGateCardComponent,
    OnboardingAnchorDirective,
    OnboardingHelpDirective,
    WizardHelpButtonComponent,
    NgTemplateOutlet,
    HlmTabsImports,
    HlmSkeleton,
  ],
  viewProviders: [
    provideIcons({
      lucideCheck,
      lucideCircleHelp,
      lucideCirclePlay,
      lucideCopy,
      lucideEye,
      lucideLock,
      lucidePenLine,
      lucideUsers,
      lucideX,
    }),
  ],
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <div class="mb-4 flex items-center justify-between gap-1.5 md:mb-6.5">
          <div class="flex min-w-0 items-center gap-2.5">
            <h2 class="text-xl font-semibold leading-[1.3] tracking-[-0.015em] text-ink md:text-2xl">{{ title() }}</h2>
            <!-- Карточка про выдачу класса стоит у заголовка окна. В «Отправить организатору» разговор
                 другой — там «?» живёт только у самой ссылки организатора, ниже. -->
            <!-- вопрос: окно «Отправить заказчику» → заголовок окна -->
            @if (titleHelp(); as helpId) {
              <app-wizard-help-button [helpId]="helpId" />
            }
          </div>
          <button
            type="button"
            (click)="close()"
            aria-label="Закрыть"
            class="grid size-9 shrink-0 place-items-center rounded-[var(--r-s)] text-c2
                 transition-colors hover:bg-acc-softer hover:text-acc focus-visible:bg-acc-softer focus-visible:text-acc
                 focus-visible:outline-none pointer-coarse:size-11 dlg-phone:hidden"
          >
            <ng-icon name="lucideX" class="text-xl" />
          </button>
        </div>

        <div class="flex flex-col gap-4 md:gap-6">
          @if (groupMode()) {
            <div class="flex items-start gap-3 rounded-[var(--r-l)] border border-acc-line bg-acc-softer px-3 py-2.5 md:p-4">
              <span
                class="grid size-8 shrink-0 place-items-center rounded-[var(--r-l)] bg-acc-soft text-base text-acc2 md:size-10 md:text-xl"
              >
                <ng-icon name="lucideUsers" />
              </span>
              <p class="text-sm leading-normal text-c2">
                @if (organizerView()) {
                  Отправьте эту ссылку в чат класса. Каждый выберет свои фотографии и утвердит макет своего альбома.
                } @else {
                  Отправьте эту ссылку в чат класса — каждый выберет себя, откроет свой макет и утвердит альбом. Следить за
                  прогрессом можно во вкладке
                  <button type="button" (click)="openAlbums()" class="font-semibold text-acc2 hover:underline">альбомы</button>.
                }
              </p>
            </div>
          }

          @if (organizerOnly()) {
            <div
              class="flex items-start gap-3.25 rounded-[var(--r-l)] border border-acc-line bg-acc-softer px-3 py-2.5 md:px-4 md:py-3.5"
            >
              <span
                class="grid size-8 shrink-0 place-items-center rounded-[var(--r-l)] bg-acc-soft text-base text-acc2 md:size-10 md:text-xl"
              >
                <ng-icon name="lucidePenLine" />
              </span>
              <div class="flex-1">
                <p class="text-sm font-semibold leading-[1.4] text-ink">Организатор заполнит имена за вас</p>
                <p class="mt-0.75 text-sm leading-normal text-c2">
                  Отправьте ссылку — организатор проставит <span class="font-semibold text-acc2">имена и фамилии</span>
                  персонам, и они сами подтянутся в альбомы проекта.
                </p>
              </div>
            </div>
          }

          @if (customerGate()) {
            <app-cover-gate-card (fillCover)="openMasterCover()" />
          }

          @if (customerMode() && !customerGate()) {
            <!-- «Отправить заказчику»: одна кнопка копирования на вкладку, ученики первыми. -->
            <div hlmTabs [tab]="tab()" (tabActivated)="selectTab($event)" class="gap-5">
              <div hlmTabsList variant="segment" aria-label="Кому отправить ссылку">
                <button hlmTabsTrigger="students">Ученикам</button>
                <button hlmTabsTrigger="organizer">Организатору</button>
              </div>
              <div hlmTabsContent="students" class="flex flex-col gap-4 md:gap-6">
                <ng-container [ngTemplateOutlet]="classArea" />
              </div>
              <div hlmTabsContent="organizer" class="flex flex-col">
                <p class="text-base leading-normal text-ink">
                  Отправьте ссылку организатору — он проставит имена учеников и педагогов
                </p>
                <button
                  hlmBtn
                  type="button"
                  size="xl"
                  class="mt-6 w-full"
                  [disabled]="coordinatorPending() || !coordinatorUrl()"
                  (click)="copyTabLink('organizer')"
                >
                  @if (copiedTab() === 'organizer') {
                    <ng-icon name="lucideCheck" />
                    Ссылка для организатора скопирована
                  } @else {
                    Скопировать ссылку для организатора
                  }
                </button>
                <button
                  hlmBtn
                  type="button"
                  variant="link"
                  size="sm"
                  class="mx-auto mt-2.5 gap-2 font-medium"
                  [onboardingHelp]="'faces-organizer-link'"
                >
                  <ng-icon name="lucideCirclePlay" class="text-base" />
                  Что увидит организатор
                </button>
              </div>
            </div>
          }

          @if (showCoordinatorLink() && !customerMode()) {
            <div class="flex flex-col gap-1.25">
              <hlm-share-link-field
                label="Ссылка для организатора"
                [value]="coordinatorPending() ? null : coordinatorUrl()"
                copyLabel="Скопировать ссылку для организатора"
                (copied)="trackLinkCopied('coordinator')"
              >
                <!-- Свой «?» у блока организатора: заголовок окна рассказывает про выдачу класса. -->
                <button
                  hint
                  hlmBtn
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Как это работает"
                  class="-ml-0.25 size-7.5 shrink-0 rounded-full text-c2 hover:text-ink"
                  [onboardingHelp]="'faces-organizer-link'"
                >
                  <ng-icon name="lucideCircleHelp" class="text-base" />
                </button>
              </hlm-share-link-field>
            </div>
          }

          @if (!customerMode()) {
            <ng-container [ngTemplateOutlet]="classArea" />
          }
        </div>

        <ng-template #classArea>
          <!-- Организатор прав фотографа не получает (Границы эпика): в ОБОИХ запертых состояниях —
               ни мастера, ни заполненной обложки — он видит один и тот же простой текст без действий,
               вместо «соберите мастер-альбом» и карточки обложки. -->
          @if (showClassLink() && classLinkBlocked() && organizerView()) {
            <div class="flex flex-col gap-1.25">
              <span class="px-0.5 text-xs leading-4 text-c3">{{ blockedLabel() }}</span>
              <div
                class="flex items-start gap-3 rounded-[var(--r-l)] border border-warn-line bg-warn-soft px-3 py-2.5 md:px-4 md:py-3.5"
              >
                <span
                  class="grid size-8 shrink-0 place-items-center rounded-[var(--r-l)] bg-warn-soft text-base text-warn md:size-9 md:text-lg"
                >
                  <ng-icon name="lucideLock" />
                </span>
                <div class="flex-1">
                  <p class="text-sm font-semibold leading-[1.4] text-ink">Ссылки ещё не готовы</p>
                  <p class="mt-0.75 text-sm leading-normal text-c2">
                    Фотограф собирает первый альбом — ссылки откроются, когда он закончит.
                  </p>
                </div>
              </div>
            </div>
          }
          @if (showClassLink() && masterMissing() && !organizerView()) {
            <div class="flex flex-col gap-1.25">
              <span class="px-0.5 text-xs leading-4 text-c3">{{ blockedLabel() }}</span>
              <div
                class="flex items-start gap-3 rounded-[var(--r-l)] border border-warn-line bg-warn-soft px-3 py-2.5 md:px-4 md:py-3.5"
              >
                <span
                  class="grid size-8 shrink-0 place-items-center rounded-[var(--r-l)] bg-warn-soft text-base text-warn md:size-9 md:text-lg"
                >
                  <ng-icon name="lucideLock" />
                </span>
                <div class="flex-1">
                  <p class="text-sm font-semibold leading-[var(--leading-compact)] text-ink">Сначала соберите эталонный альбом</p>
                  <p class="mt-0.75 text-sm leading-normal text-c2">
                    Соберите первый альбом во вкладке
                    <button type="button" (click)="openAlbums()" class="font-semibold text-acc2 hover:underline">альбомы</button>
                    — он станет эталоном проекта, и ссылки с ПИНами откроются.
                  </p>
                </div>
              </div>
            </div>
          }
          @if (showClassLink() && !masterMissing() && coverIncomplete() && !customerMode() && !organizerView()) {
            <div class="flex flex-col gap-1.25">
              <span class="px-0.5 text-xs leading-4 text-c3">{{ blockedLabel() }}</span>
              <app-cover-gate-card (fillCover)="openMasterCover()" />
            </div>
          }
          <!-- Пока режим входа не известен, ссылка класса держит скелет: иначе она мигнёт и сменится
               личными ссылками или ПИНами. -->
          @if (customerMode() && !classLinkBlocked() && (classLinkPending() || !linksMode())) {
            <div class="flex flex-col">
              <!-- Строки скелета стоят в высоту строк текста: окно не растёт, когда подпись приходит. -->
              @if (subtitlePending()) {
                <div class="flex flex-col" aria-hidden="true">
                  @for (bar of subtitleBars(); track bar) {
                    <div class="flex h-6 items-center">
                      @if ($last) {
                        <div hlmSkeleton class="h-4 w-2/3"></div>
                      } @else {
                        <div hlmSkeleton class="h-4 w-full"></div>
                      }
                    </div>
                  }
                </div>
              } @else {
                <p class="text-base leading-normal text-ink">{{ studentsSubtitle() }}</p>
              }
              <div class="mt-6 flex items-center gap-2">
                <button
                  hlmBtn
                  type="button"
                  size="xl"
                  class="flex-1"
                  [disabled]="classLinkPending() || !classUrl()"
                  (click)="copyTabLink('students')"
                >
                  @if (copiedTab() === 'students') {
                    <ng-icon name="lucideCheck" />
                    Ссылка для учеников скопирована
                  } @else {
                    Скопировать ссылку для учеников
                  }
                </button>
                <!-- Квадрат высотой кнопки копирования: в одном ряду одна высота. -->
                <button
                  hlmBtn
                  type="button"
                  variant="outline"
                  size="xl"
                  class="aspect-square px-0"
                  (click)="openClassPreview()"
                  [disabled]="classLinkPending() || !classUrl()"
                  aria-label="Посмотреть как клиент"
                  hlmTooltip="Посмотреть как клиент"
                  position="top"
                >
                  <ng-icon name="lucideEye" class="text-xl" />
                </button>
              </div>
              <button
                hlmBtn
                type="button"
                variant="link"
                size="sm"
                class="mx-auto mt-2.5 gap-2 font-medium"
                [onboardingHelp]="'albums-students-pick'"
              >
                <ng-icon name="lucideCirclePlay" class="text-base" />
                Что увидят ученики
              </button>
            </div>
          } @else if (showClassLink() && !classLinkBlocked() && (classLinkPending() || (!linksMode() && !pinsOnly()))) {
            <div class="flex flex-col gap-1.25">
              <hlm-share-link-field
                label="Ссылка для класса/группы"
                [value]="classLinkPending() ? null : classUrl()"
                copyLabel="Скопировать ссылку для класса/группы"
                (copied)="trackLinkCopied('class')"
              >
                <!-- «?» у ссылки класса — ролик про то, как эту ссылку видит ученик. Стоит и на скелетоне,
                     чтобы строка подписи не прыгала по высоте, когда ссылка догрузится. -->
                <button
                  hint
                  hlmBtn
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Как это работает"
                  class="-ml-0.25 size-7.5 shrink-0 rounded-full text-c2 hover:text-ink"
                  [onboardingHelp]="'albums-view-as-client'"
                >
                  <ng-icon name="lucideCircleHelp" class="text-base" />
                </button>
              </hlm-share-link-field>
            </div>
          }
          @if (showClassLink() && !classLinkBlocked() && !classLinkPending() && linksMode() && !pinsOnly()) {
            <div class="flex flex-col gap-1.25">
              <span class="px-0.5 text-xs leading-4 text-c3">Индивидуальные ссылки</span>
              @if (personsLoading()) {
                <div class="h-30 animate-pulse rounded-[var(--r-l)] border border-line2 bg-bg2"></div>
                <!-- The loaded branch ends with an h-11 copy CTA — reserve its footprint too. -->
                <div class="mt-1 h-11 animate-pulse rounded-[var(--r)] border border-line2 bg-bg2"></div>
              } @else if (linkedPersons().length === 0 && organizerView()) {
                <!-- Экран «Лица» организатору закрыт (organizerSectionRedirectGuard вернёт его к ученикам),
                     поэтому здесь тот же спокойный текст запрета, что и в остальных состояниях, без действия. -->
                <app-personal-links-stub>Ссылки ещё не готовы</app-personal-links-stub>
              } @else if (linkedPersons().length === 0) {
                <app-personal-links-stub>
                  Вход по общей ссылке отключён. Личные ссылки — на экране
                  <button type="button" (click)="openFaces()" class="font-semibold text-acc2 hover:underline">лица</button>.
                </app-personal-links-stub>
              } @else {
                <!-- Selectable text; у организатора каждая строка несёт свою кнопку копирования (s1-ac5.1),
                     у фотографа строка остаётся прежней — copy живёт только в CTA ниже. -->
                <div class="rounded-[var(--r-l)] border border-line2 bg-bg2">
                  <div class="flex max-h-42 flex-col gap-1 overflow-y-auto px-3.5 py-2.5">
                    <p class="text-sm font-semibold leading-normal text-ink">{{ projectName() }}</p>
                    @for (person of linkedPersons(); track person.id) {
                      <p class="flex items-baseline gap-2 text-sm leading-normal">
                        <span class="shrink-0 text-ink">{{ personName(person) }}</span>
                        <span class="min-w-0 truncate text-c2">{{ personLink(person) }}</span>
                        @if (customerMode() && person.role === 'student') {
                          <!-- якорь: окно «Отправить заказчику» → значок глаза в строке ученика -->
                          <button
                            type="button"
                            (click)="openPersonPreview(person)"
                            [onboardingAnchor]="viewAsClientAnchor(person.id)"
                            [class]="iconBtnClass() + ' self-center'"
                            aria-label="Посмотреть как клиент"
                            hlmTooltip="Посмотреть как клиент"
                            position="top"
                          >
                            <ng-icon name="lucideEye" />
                          </button>
                        }
                        @if (organizerView()) {
                          <button
                            type="button"
                            (click)="copyPersonLink(person)"
                            [class]="iconBtnClass(copiedRowId() === person.id) + ' self-center'"
                            [attr.aria-label]="
                              copiedRowId() === person.id ? 'Ссылка скопирована' : 'Скопировать — ' + personName(person)
                            "
                          >
                            @if (copiedRowId() === person.id) {
                              <ng-icon name="lucideCheck" />
                            } @else {
                              <ng-icon name="lucideCopy" />
                            }
                          </button>
                        }
                      </p>
                    }
                  </div>
                </div>
                <button
                  hlmBtn
                  type="button"
                  (click)="copyLinksList()"
                  class="mt-1 flex h-11 w-full items-center justify-center gap-2 text-sm font-semibold"
                >
                  @if (linksCopied()) {
                    <ng-icon name="lucideCheck" />
                    Скопировано
                  } @else {
                    Скопировать список ссылок
                  }
                </button>
              }
            </div>
          }
          @if (showClassLink() && !classLinkBlocked() && !classLinkPending() && pinsOnly()) {
            <div class="flex flex-col gap-1.25">
              <span class="px-0.5 text-xs leading-4 text-c3">ПИН-коды</span>
              @if (personsLoading()) {
                <div class="h-30 animate-pulse rounded-[var(--r-l)] border border-line2 bg-bg2"></div>
                <!-- The loaded branch ends with an h-11 copy CTA — reserve its footprint too. -->
                <div class="mt-1 h-11 animate-pulse rounded-[var(--r)] border border-line2 bg-bg2"></div>
              } @else if (pinnedPersons().length === 0) {
                <app-personal-links-stub>
                  ПИНы появятся здесь, когда ученики зададут их при первом входе в свой альбом.
                </app-personal-links-stub>
              } @else {
                <!-- Plain selectable text; each row carries its own copy button. -->
                <div class="rounded-[var(--r-l)] border border-line2 bg-bg2">
                  <div class="flex max-h-42 flex-col gap-0.5 overflow-y-auto px-3.5 py-2.5">
                    <p class="text-sm font-semibold leading-normal text-ink">{{ projectName() }}</p>
                    <p class="truncate text-sm leading-normal text-c2">{{ classUrl() }}</p>
                    @for (person of pinnedPersons(); track person.id) {
                      <div class="flex items-center gap-2">
                        <p class="flex min-w-0 flex-1 items-baseline gap-2 text-sm leading-normal">
                          <span class="min-w-0 truncate text-ink">{{ personName(person) }}</span>
                          <span class="shrink-0 text-c2">ПИН: {{ person.pin }}</span>
                        </p>
                        <button
                          type="button"
                          (click)="copyPin(person)"
                          [class]="iconBtnClass(copiedRowId() === person.id)"
                          [attr.aria-label]="
                            copiedRowId() === person.id ? 'ПИН скопирован' : 'Скопировать ПИН — ' + personName(person)
                          "
                        >
                          @if (copiedRowId() === person.id) {
                            <ng-icon name="lucideCheck" />
                          } @else {
                            <ng-icon name="lucideCopy" />
                          }
                        </button>
                      </div>
                    }
                  </div>
                </div>
                <button
                  hlmBtn
                  type="button"
                  (click)="copyPinsList()"
                  class="mt-1 flex h-11 w-full items-center justify-center gap-2 text-sm font-semibold"
                >
                  @if (linksCopied()) {
                    <ng-icon name="lucideCheck" />
                    Скопировано
                  } @else {
                    Скопировать список ПИНов
                  }
                </button>
              }
            </div>
          }
        </ng-template>

        @if (organizerOnly()) {
          <div class="mt-7 flex gap-2.5">
            <button
              hlmBtn
              type="button"
              variant="outline"
              (click)="openPreview()"
              [disabled]="!previewReady()"
              class="flex h-12 flex-1 items-center justify-center gap-2 border-acc-line bg-acc-softer text-sm font-semibold text-acc2 hover:border-acc-line"
            >
              <ng-icon name="lucideEye" class="text-xl" />
              Предпросмотр
            </button>
          </div>
        }
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class OrganizerShareDialogComponent {
  protected readonly ctx = injectBrnDialogContext<OrganizerShareContext>();
  private readonly dialogRef = inject(BrnDialogRef);
  private readonly clipboard = inject(Clipboard);
  private readonly facade = inject(ProjectsFacade);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly shareAnalytics = inject(ShareAnalyticsService);
  protected readonly organizerOnly = computed(() => this.ctx.organizerOnly === true);
  protected readonly groupMode = computed(() => this.ctx.groupMode === true);
  protected readonly linksOnly = computed(() => this.ctx.linksOnly === true);
  protected readonly pinsOnly = computed(() => this.ctx.pinsOnly === true);
  protected readonly organizerView = computed(() => this.ctx.organizerView === true);
  // «Отправить заказчику» — the owner's view with BOTH links; each link carries its own explainer banner.
  protected readonly customerMode = computed(() => isCustomerShare(this.ctx));

  protected readonly title = computed(() => {
    if (this.linksOnly()) return 'Личные ссылки';
    if (this.pinsOnly()) return 'ПИН-коды';
    if (this.groupMode()) return 'Отправить группе';
    return this.organizerOnly() ? 'Отправить организатору' : 'Отправить заказчику';
  });

  /** «?» у заголовка = разговор про выдачу класса. Окно организатора о ней не говорит — там значок
   *  стоит у самой ссылки организатора, и второй у заголовка был бы той же карточкой дважды. Глазами
   *  организатора коуча нет вовсе: карточки онбординга — фотографу. */
  protected readonly titleHelp = computed(() => (this.organizerOnly() || this.organizerView() ? null : 'albums-send-class-link'));

  // The organizer link is for the organizer — hide it in group mode (they already ARE the organizer).
  protected readonly showCoordinatorLink = computed(
    () => !this.groupMode() && !this.linksOnly() && !this.pinsOnly() && !this.customerGate(),
  );

  // The class/group link: the owner's «Заказчику» view AND the organizer's «Группе» view.
  protected readonly showClassLink = computed(() => !this.organizerOnly() || this.groupMode());
  /**
   * Один вердикт на всю выдачу ученикам: `checking` — ещё проверяем (пока держим скелет),
   * `noMaster` — мастер-альбома нет ИЛИ он не читается, `true|false` — есть ли на его обложке пустая
   * надпись (`false` = отдавать можно).
   *
   * Обложка мастер-альбома печатается всему классу, а ученик её не правит. FAIL-CLOSED: непрочитанные
   * настройки и мастер, который не отдаётся (удалён, 404), запирают выдачу так же, как отсутствующий
   * мастер — молчаливое «всё хорошо» на ошибке уже отдало классу ссылку на пустую обложку. Отсутствие
   * мастера ссылку организатора не трогает: имена вписывают до того, как собран мастер.
   */
  private readonly coverVerdict = signal<'checking' | 'noMaster' | boolean>('checking');
  private readonly coverChecking = computed(() => this.coverVerdict() === 'checking');
  protected readonly masterMissing = computed(() => this.coverVerdict() === 'noMaster');

  protected readonly coverIncomplete = computed(() => this.coverVerdict() === true);
  /** В «Отправить заказчику» пустая обложка убирает ОБЕ ссылки — на их месте одна карточка гейта. */
  protected readonly customerGate = computed(() => this.customerMode() && this.coverIncomplete());
  /** Обе причины запрета в одном ответе — ветки ссылок спрашивают только его. */
  protected readonly classLinkBlocked = computed(() => this.masterMissing() || this.coverIncomplete());
  // Запрет встаёт на место того, за чем пришёл читатель: в «ПИН-кодах» — ПИНы, в личных ссылках — они.
  protected readonly blockedLabel = computed(() => {
    if (this.pinsOnly()) return 'ПИН-коды';
    return this.linksOnly() || this.linksMode() ? 'Индивидуальные ссылки' : 'Ссылка для класса/группы';
  });

  private readonly flowMode = computed(() => shareFlowMode(this.facade, this.ctx.projectId));

  private readonly selectionAsk = computed<SelectionAsk | null>(() => {
    const structure = this.facade.masterStructureByProject()[this.ctx.projectId];
    return structure?.state === 'ready' && structure.capacity ? selectionAskCounts(structure.capacity) : null;
  });

  protected readonly studentsSubtitle = computed(() =>
    studentsShareSubtitle(this.flowMode(), this.selectionAsk(), this.ctx.stepCounts ?? null),
  );

  // A read of this open settled with nothing to show — the subtitle takes today's fallback line.
  private readonly subtitleReadFailed = signal(false);
  /** Cold cache: flow unknown, or the structure it needs not read yet — skeleton, never a generic line. */
  protected readonly subtitlePending = computed(() => {
    if (this.subtitleReadFailed()) return false;
    const flow = this.flowMode();
    if (!flow) return true;
    if (flow === 'auto' || allPicked(this.ctx.stepCounts ?? null)) return false;
    return !this.facade.masterStructureByProject()[this.ctx.projectId];
  });

  /** Skeleton lines mirror the resolved text: a picked student adds the second sentence. */
  protected readonly subtitleBars = computed(() => ((this.ctx.stepCounts?.selected ?? 0) > 0 ? [0, 1, 2] : [0, 1]));

  protected readonly tab = signal<ShareTab>(this.ctx.tab ?? 'students');
  // Одна вкладка отчитывается «скопирована» за раз; через 3 с кнопка возвращает свою подпись.
  protected readonly copiedTab = signal<ShareTab | null>(null);
  private copiedTabTimer: ReturnType<typeof setTimeout> | null = null;

  // Seeded from the context when the caller pre-fetched, else filled by the constructor's own fetch.
  private readonly link = signal(this.ctx.link ?? '');
  private readonly coordinatorCode = signal(this.ctx.coordinatorCode ?? '');
  // The links are still loading (no pre-fetch AND the fetch hasn't returned) — show a skeleton row.
  protected readonly linksLoading = signal(this.ctx.link === undefined && this.ctx.coordinatorCode === undefined);
  // Гейт заказчика снимает и эту ссылку, поэтому до вердикта по обложке она держит скелет — иначе мигнёт.
  protected readonly coordinatorPending = computed(() => this.linksLoading() || (this.customerMode() && this.coverChecking()));
  private readonly loginMode = signal<StudentLoginMode | null>(null);
  // The class-link block can't pick a branch until the mode is known — hold the skeleton, or the
  // link would flash and then swap to the personal-links stub.
  private readonly modeLoading = signal(true);
  // Проверка обложки идёт в том же ожидании: иначе ссылка мигнёт до вердикта и её успеют скопировать.
  protected readonly classLinkPending = computed(() => this.linksLoading() || this.modeLoading() || this.coverChecking());
  protected readonly linksMode = computed(() => this.loginMode() === 'links');

  // Personal-links window (links mode): the staff roster read also lazily mints missing tokens server-side.
  protected readonly personsLoading = signal(false);
  // The store holds ONE page (50). A class larger than that would copy out a truncated key list, so the
  // dialog pulls the whole roster and lets the store's fresher rows (a pin just regenerated here) win.
  private readonly fullRoster = signal<Person[] | null>(null);
  // `personsLoading` only tracks the first-page refresh; the copy-all lists are built from the FULL
  // roster, so they stay pending on their own state — a class over one page would otherwise copy a
  // silently truncated list the moment the first page landed.
  private readonly fullRosterState = signal<'idle' | 'loading' | 'ready' | 'failed'>('idle');
  protected readonly copyAllPending = computed(() => this.fullRosterState() === 'loading');
  protected readonly copyAllFailed = computed(() => this.fullRosterState() === 'failed');
  private readonly rosterPersons = computed<Person[]>(() => {
    const loaded = this.facade.personsProjectId() === this.ctx.projectId;
    const page = loaded ? [...this.facade.students(), ...this.facade.teachers()] : [];
    const full = this.fullRoster();
    if (!full) return page;

    const fresher = new Map(page.map(person => [person.id, person]));
    return full.map(person => fresher.get(person.id) ?? person);
  });

  protected readonly linkedPersons = computed<Person[]>(() => this.rosterPersons().filter(person => !!person.linkToken));

  protected readonly pinnedPersons = computed<Person[]>(() => this.rosterPersons().filter(person => !!person.pin));

  /**
   * Строка, на глазе которой висит подсказка «посмотрите глазами ученика»: первая в списке, чей
   * альбом уже собран и ждёт утверждения. Раньше этого момента смотреть ещё нечего.
   */
  private readonly viewAsClientPersonId = computed<string | null>(() => {
    const designReady = new Set(
      (this.facade.albumsByProject()[this.ctx.projectId] ?? [])
        .filter(album => album.status === 'designReady' && album.personId)
        .map(album => album.personId),
    );

    return this.linkedPersons().find(person => person.role === 'student' && designReady.has(person.id))?.id ?? null;
  });

  protected readonly projectName = computed(() => this.facade.currentProjectName() ?? '');
  protected readonly linksCopied = signal(false);
  private linksCopiedTimer: ReturnType<typeof setTimeout> | null = null;
  // Одна строка списка отчиталась «скопировано» — и в ПИНах, и в личных ссылках (блоки не сосуществуют).
  protected readonly copiedRowId = signal<string | null>(null);
  private copiedRowTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.shareAnalytics.modalOpened(this.ctx.projectId);

    if (this.showClassLink()) {
      void this.facade
        .loadProjectBaseSettings(this.ctx.projectId)
        .then(() => {
          const flow = this.flowMode();
          if (this.customerMode() && flow && flow !== 'auto' && !allPicked(this.ctx.stepCounts ?? null))
            void this.readStructure();
          return this.checkMasterCover();
        })
        // Настройки не прочитались — мастера для нас нет: запрет, а не открытая ссылка.
        .catch(() => {
          this.subtitleReadFailed.set(true);
          this.coverVerdict.set('noMaster');
        });
      this.facade
        .getStudentLoginMode(this.ctx.projectId)
        .then(mode => {
          this.loginMode.set(mode);
          if (mode === 'links') this.loadPersons();
        })
        .catch(() => undefined)
        .finally(() => this.modeLoading.set(false));
      if (this.pinsOnly()) this.loadPersons();
    } else {
      this.modeLoading.set(false);
    }

    if (this.linksLoading()) {
      this.facade
        .getProjectShare(this.ctx.projectId)
        .then(share => {
          this.link.set(share.link);
          this.coordinatorCode.set(share.coordinatorCode);
        })
        .catch(() => this.toast.error('Не удалось получить ссылки проекта'))
        .finally(() => this.linksLoading.set(false));
    }
  }

  // No link (share fetch failed) = no URL: a bare `/client/` would copy as a dead link.
  protected readonly classUrl = computed(() => (this.link() ? clientZoneUrl(this.link()) : ''));
  // The coordinator flow lives in THIS app — the link must carry our own origin (works on any stand).
  protected readonly coordinatorUrl = computed(() =>
    this.coordinatorCode()
      ? `${window.location.origin}/auth/projects/${this.ctx.projectId}/coordinator/${this.coordinatorCode()}`
      : '',
  );

  protected trackLinkCopied(field: 'coordinator' | 'class'): void {
    this.shareAnalytics.linkCopied({ projectId: this.ctx.projectId, type: field });
  }

  protected selectTab(tab: string): void {
    this.tab.set(tab === 'organizer' ? 'organizer' : 'students');
  }

  protected copyTabLink(tab: ShareTab): void {
    const url = tab === 'students' ? this.classUrl() : this.coordinatorUrl();
    if (!url) return;
    this.clipboard.copy(url);
    this.trackLinkCopied(tab === 'students' ? 'class' : 'coordinator');
    this.copiedTab.set(tab);
    if (this.copiedTabTimer) clearTimeout(this.copiedTabTimer);
    this.copiedTabTimer = setTimeout(() => this.copiedTab.set(null), COPIED_MS);
  }

  /** Одна форма для всех иконочных кнопок диалога — копирование строки и «посмотреть как клиент». */
  protected iconBtnClass(active = false): string {
    const base = 'grid size-7.5 shrink-0 place-items-center rounded-lg';
    return active ? `${base} text-ok` : `${base} text-acc`;
  }

  /** Один живой якорь на весь список: остальным строкам он не положен. */
  protected viewAsClientAnchor(personId: string): string | null {
    return personId === this.viewAsClientPersonId() ? 'albums-view-as-client' : null;
  }

  private loadPersons(): void {
    this.personsLoading.set(true);
    void this.facade
      .refreshPersons(this.ctx.projectId)
      .catch(() => undefined)
      .finally(() => this.personsLoading.set(false));
    // Pages past the first one only matter for the copy-all lists — they arrive without blocking the view.
    this.fullRosterState.set('loading');
    void this.facade
      .fullPersonRoster(this.ctx.projectId)
      .then(roster => {
        if (!roster) {
          this.fullRosterState.set('failed');
          return;
        }

        this.fullRoster.set(roster);
        this.fullRosterState.set('ready');
      })
      .catch(() => this.fullRosterState.set('failed'));
  }

  /** True while a copy-all would ship an incomplete list — the button must not report success then. */
  private guardCopyAll(): boolean {
    if (this.copyAllPending()) {
      this.toast.info('Список ещё загружается — повторите через секунду');
      return false;
    }

    if (this.copyAllFailed()) {
      this.toast.error('Не удалось загрузить весь список — попробуйте ещё раз');
      return false;
    }

    return true;
  }

  protected personName(person: Person): string {
    return [person.surname, person.firstName].filter(Boolean).join(' ') || '(без имени)';
  }

  protected personLink(person: Person): string {
    return person.linkToken ? personalLinkUrl(person.linkToken) : '';
  }

  protected copyLinksList(): void {
    if (!this.guardCopyAll()) return;
    const { text, count } = formatKeysClipboard(this.projectName(), null, this.linkedPersons(), 'links');
    if (count === 0) {
      this.toast.info('Нет ссылок для копирования');
      return;
    }

    this.clipboard.copy(text);
    this.flashListCopied();
  }

  protected copyPinsList(): void {
    if (!this.guardCopyAll()) return;
    const { text, count } = formatKeysClipboard(this.projectName(), this.classUrl(), this.pinnedPersons(), 'pin');
    if (count === 0) {
      this.toast.info('Нет ПИНов для копирования');
      return;
    }

    this.clipboard.copy(text);
    this.flashListCopied();
  }

  // One person's pin, wrapped in the same header (project name + login URL) — ready to forward as is.
  protected copyPin(person: Person): void {
    const { text, count } = formatKeysClipboard(this.projectName(), this.classUrl(), [person], 'pin');
    this.clipboard.copy(count > 0 ? text : (person.pin ?? ''));
    this.flashRowCopied(person.id);
  }

  // Личная ссылка одного ученика: организатор пересылает её лично, поэтому в буфер идёт только сам url.
  protected copyPersonLink(person: Person): void {
    this.clipboard.copy(this.personLink(person));
    this.flashRowCopied(person.id);
  }

  private flashRowCopied(personId: string): void {
    this.copiedRowId.set(personId);
    if (this.copiedRowTimer) clearTimeout(this.copiedRowTimer);
    this.copiedRowTimer = setTimeout(() => this.copiedRowId.set(null), 2000);
  }

  private flashListCopied(): void {
    this.linksCopied.set(true);
    if (this.linksCopiedTimer) clearTimeout(this.linksCopiedTimer);
    this.linksCopiedTimer = setTimeout(() => this.linksCopied.set(false), 2000);
  }

  protected close(): void {
    this.dialogRef.close();
  }

  // A cached structure keeps its text through a failed refresh; with none cached the line falls back.
  private async readStructure(): Promise<void> {
    if (!(await this.facade.masterStructure(this.ctx.projectId))) this.subtitleReadFailed.set(true);
  }

  /** Читаем обложку мастера тем же законом, что судит утверждение и сервер. Нечитаемый мастер
   *  (`null` от проверки, отказ запроса) отвечает так же, как отсутствующий: отдавать нельзя. */
  private async checkMasterCover(): Promise<void> {
    const masterAlbumId = this.facade.baseSettingsByProject()[this.ctx.projectId]?.photoBook?.masterAlbumId;
    if (!masterAlbumId) {
      this.coverVerdict.set('noMaster');
      return;
    }

    try {
      // Рождённая с проектом строка-шаблон (T2.1) держит указатель, но альбомом не является:
      // без модели мастера нет — тот же запрет, что и без указателя.
      // Диалог открывают и из списка проектов — греем корзину без выбора проекта, чтобы не
      // перехватить currentProjectId у проекта, в который организатор уже ушёл.
      await this.facade.ensureAlbumsPresent(this.ctx.projectId);
      const master = (this.facade.albumsByProject()[this.ctx.projectId] ?? []).find(album => album.id === masterAlbumId);
      if (!isUsableAlbum(master)) {
        this.coverVerdict.set('noMaster');
        return;
      }

      this.coverVerdict.set((await this.facade.albumCoverHasBlankTexts(this.ctx.projectId, masterAlbumId)) ?? 'noMaster');
    } catch {
      this.coverVerdict.set('noMaster');
    }
  }

  /** Гейт обложки: тот же дип-линк, что у гейта утверждения — обложка во вкладке «Дизайнер», где пустое поле можно и заполнить, и удалить. */
  protected openMasterCover(): void {
    const masterAlbumId = this.facade.baseSettingsByProject()[this.ctx.projectId]?.photoBook?.masterAlbumId;
    if (!masterAlbumId) return;
    this.dialogRef.close();
    void this.router.navigate(['/editor', this.ctx.projectId, masterAlbumId], {
      queryParams: { mode: 'designer', focus: 'cover' },
    });
  }

  // The «альбомы» word in the group banner: close the modal and open the Albums section in place.
  protected openAlbums(): void {
    this.dialogRef.close();
    void this.router.navigate(['/projects', this.ctx.projectId, 'albums']);
  }

  // The «лица» word in the personal-links stub: the per-person links live on the Faces screen.
  protected openFaces(): void {
    this.dialogRef.close();
    void this.router.navigate(['/projects', this.ctx.projectId, 'faces']);
  }

  // «Предпросмотр»: open the organizer's own link so the photographer sees the project exactly as the
  // organizer will (the coordinator link drops the owner into organizer-preview mode). Same tab, not a
  // new one: «Выйти из режима» returns the tab itself, and a second tab would only pile up. The current
  // surface rides along as `return` so the exit lands here — coordinator-login + shell.exitOrganizerPreview.
  protected openPreview(): void {
    this.openAsGuest(this.coordinatorUrl());
  }

  /** «Посмотреть как клиент» в строке ученика — его личная ссылка, его собственный экран. */
  protected openPersonPreview(person: Person): void {
    this.openAsGuest(this.personLink(person));
  }

  /** «Посмотреть как клиент» у ссылки класса — путь ученика по общей ссылке. */
  protected openClassPreview(): void {
    this.openAsGuest(this.classUrl());
  }

  private openAsGuest(url: string): void {
    if (!url) return;
    this.dialogRef.close();
    this.redirect(`${url}?return=${encodeURIComponent(this.router.url)}`);
  }

  /** Жёсткий переход, а не Router: гварды и роль вкладки решаются на загрузке — тем же способом
   *  предпросмотр из себя и выходит (exitClientPreview). Вынесено ради спеков: window.location.assign
   *  в этом jsdom не подменяется. */
  protected redirect(url: string): void {
    window.location.assign(url);
  }

  // Preview stays disabled until the coordinator link has actually loaded.
  protected readonly previewReady = computed(() => !!this.coordinatorUrl());
}
